import React from 'react';
import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, Text, mergeClassNames } from '@clockout-dev/design-elements';
import styles from './styles/rulesPanel.module.scss';

/**
 * Shared shell for a vertical list of editable rules ("rows") with an inline
 * "add row" link button and an optional validation message. Used by the
 * Advanced Fee Configurations tabs (`VolumeTiersPanel`, `ProductTypesPanel`,
 * `FreeAdvancesPanel`) to keep their wrapping div, error text, and add
 * button in sync.
 *
 * Props:
 * - `children` — the rendered rule rows
 * - `onAdd` — click handler for the add button
 * - `addLabel` — text shown next to the `+` icon
 * - `errorMessage` — when truthy, rendered as a red inline validation message
 *   above the add button. Pass a falsy value to hide it.
 * - `emptyState` — node rendered in place of `children` when there are no
 *   rule rows. Lets each panel show its own `EmptyState` placeholder
 *   without recomputing emptiness in every consumer.
 */
export default function RulesPanel({ children, onAdd, addLabel, errorMessage, emptyState, classNames }) {
    const isEmpty = React.Children.count(children) === 0;

    return (
        <div className={mergeClassNames([styles.panel, classNames?.root])}>
            {isEmpty && emptyState ? emptyState : children}
            {errorMessage ? (
                <Text as="span" size="xs" className={styles.errorMessage}>
                    {errorMessage}
                </Text>
            ) : null}
            <Button
                type="button"
                size="s"
                variant="text"
                className={styles.addRowButton}
                onClick={onAdd}
            >
                <PlusCircleOutlined />
                {addLabel}
            </Button>
        </div>
    );
}
